import React, {createContext, ReactNode, useCallback, useState} from 'react';
import {equals} from 'ramda';
import {View, StyleSheet} from 'react-native';
import {Toast, ToastProps, ToastType} from 'ui-kits/Toast';
import {spacing} from 'theme/Units';

// types
type Toast = {
  readonly id: string;
} & Omit<ToastProps, 'forceClose'>;

export interface ToastContextType {
  addToast: (
    id: string,
    title: string,
    message: string,
    type: ToastType,
    duration: number,
  ) => void;
  removeToast: (
    id: string,
  ) => void;
}

// creating context with default value
const ToastContext = createContext<ToastContextType>({
  addToast: () => {},
  removeToast: () => {},
});
const Provider = ToastContext.Provider;

// Toast Provider
export const ToastProvider: React.FC<{children: ReactNode}> = ({children}) => {
  // stack of toast such that we can have multiple toasts
  const [toasts, setToasts] = useState<Toast[]>([]);

  // function for displaying toasts
  const addToast = useCallback(
    (id: string, title: string, message: string, type: ToastType, duration: number) => {
      const _id = '1';
      const newToast = {id: _id, title, message, type, duration};
      setToasts(prev => {
        // search for existing id
        for (let i of prev) {
          if (equals(i, newToast)) {
            return prev;
          }
        }
        return [...prev, newToast];
      });
    },
    [],
  );

  // function for removing toasts
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <Provider value={{addToast, removeToast}}>
      {/* rebder the app */}
      {children}

      {/* components to display all the toast as an overlay */}
      <View style={styles.toastContainer}>
        {toasts.map(item => (
          <Toast
            key={item.id}
            {...item}
            forceClose={() => removeToast(item.id)}
          />
        ))}
      </View>
    </Provider>
  );
};

const styles = StyleSheet.create({
  toastContainer: {
    position: 'fixed',
    top: 0,
    right: 0,
    padding: spacing['4'],
    alignItems: 'flex-end',
  },
});

export default ToastContext;
